// src/firebase/config.js - REPLACE the entire file

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider,
  setPersistence, 
  browserLocalPersistence,
  indexedDBLocalPersistence 
} from 'firebase/auth';
import { 
  getFirestore, 
  enableIndexedDbPersistence, 
  enableNetwork, 
  disableNetwork, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { isPWA } from '../utils/pwaDetection';

const firebaseConfig = {
  apiKey: "AIzaSyDfQfvgNPUQPtSvJEgXkwWBxmBZgVkY5Wg",
  authDomain: "kalkode-8b160.firebaseapp.com",
  databaseURL: "https://kalkode-8b160-default-rtdb.firebaseio.com",
  projectId: "kalkode-8b160",
  storageBucket: "kalkode-8b160.firebasestorage.app",
  messagingSenderId: "357593143871",
  appId: "1:357593143871:web:8a6388d7f9e03fade1f68a",
  measurementId: "G-3Z5HWMXRKW"
};

// Initialize the app first
const app = initializeApp(firebaseConfig);

// Initialize services with error handling
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);

// ✅ UPDATED: Enhanced auth persistence with PWA detection
const initializeAuthPersistence = async () => {
  try {
    const isInstalledPWA = isPWA();
    
    if (isInstalledPWA) {
      // Use IndexedDB persistence for PWA (most persistent - survives 7+ days)
      await setPersistence(auth, indexedDBLocalPersistence);
      console.log('✅ PWA detected - Using IndexedDB persistence (7+ days)');
      
      // ✅ Store PWA mode flag
      localStorage.setItem('pwa_mode', 'true');
    } else {
      // Use localStorage for web (still persistent but less so)
      await setPersistence(auth, browserLocalPersistence);
      console.log('✅ Browser mode - Using localStorage persistence');
      
      // ✅ Clear PWA mode flag
      localStorage.setItem('pwa_mode', 'false');
    }
  } catch (error) {
    console.error('❌ Error setting auth persistence:', error);
    
    // ✅ Fallback: try localStorage if IndexedDB fails
    try {
      await setPersistence(auth, browserLocalPersistence);
      console.log('⚠️ Fallback to localStorage persistence');
    } catch (fallbackError) {
      console.error('❌ Fallback persistence also failed:', fallbackError);
    }
  }
};

// ✅ UPDATED: Enhanced Firestore persistence with better error handling
const initializeFirestorePersistence = async () => {
  try {
    const isInstalledPWA = isPWA();
    
    if (isInstalledPWA) {
      await enableIndexedDbPersistence(db, {
        // ✅ Add synchronize tabs option
        synchronizeTabs: true
      });
      console.log('✅ Firestore offline persistence enabled for PWA (with tab sync)');
    }
  } catch (error) {
    if (error.code === 'failed-precondition') {
      console.warn('⚠️ Multiple tabs open - persistence enabled in first tab only');
      // ✅ Still usable, just won't sync across tabs
    } else if (error.code === 'unimplemented') {
      console.warn('⚠️ Browser does not support persistence');
    } else {
      console.error('❌ Error enabling Firestore persistence:', error);
    }
  }
};

// ✅ NEW: Initialize persistence with retry logic
const initializePersistenceWithRetry = async () => {
  let attempts = 0;
  const maxAttempts = 3;
  
  while (attempts < maxAttempts) {
    try {
      await initializeAuthPersistence();
      await initializeFirestorePersistence();
      console.log('✅ All persistence initialized successfully');
      return;
    } catch (error) {
      attempts++;
      console.warn(`⚠️ Persistence initialization attempt ${attempts}/${maxAttempts} failed`);
      
      if (attempts < maxAttempts) {
        // Wait 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        console.error('❌ Failed to initialize persistence after all attempts');
      }
    }
  }
};

// Initialize persistence
initializePersistenceWithRetry();

// Configure Google Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Enhanced connection monitoring and error handling
let isOnline = navigator.onLine;
let reconnectAttempts = 0;
const maxReconnectAttempts = 5;

// Network status monitoring
const handleOnline = async () => {
  console.log('🌐 Network connection restored');
  isOnline = true;
  reconnectAttempts = 0;
  
  try {
    await enableNetwork(db);
    console.log('✅ Firestore connection restored');
    
    // ✅ Emit custom event for app to respond
    window.dispatchEvent(new CustomEvent('network-restored'));
  } catch (error) {
    console.error('❌ Failed to restore Firestore connection:', error);
  }
};

const handleOffline = async () => {
  console.log('📴 Network connection lost');
  isOnline = false;
  
  try {
    await disableNetwork(db);
    console.log('✅ Firestore offline mode enabled');
    
    // ✅ Emit custom event for app to respond
    window.dispatchEvent(new CustomEvent('network-lost'));
  } catch (error) {
    console.error('❌ Failed to enable offline mode:', error);
  }
};

// Add event listeners for network status
window.addEventListener('online', handleOnline);
window.addEventListener('offline', handleOffline);

// ✅ NEW: Check initial network state
if (!navigator.onLine) {
  handleOffline();
}

// Firestore connection state monitoring
const monitorFirestoreConnection = () => {
  let connectionState = 'connected';
  
  const checkConnection = async () => {
    try {
      if (!isOnline) {
        connectionState = 'offline';
        return;
      }
      
      connectionState = 'connected';
    } catch (error) {
      console.error('Connection check failed:', error);
      connectionState = 'error';
      
      // Attempt reconnection with exponential backoff
      if (reconnectAttempts < maxReconnectAttempts) {
        const delay = Math.pow(2, reconnectAttempts) * 1000;
        reconnectAttempts++;
        
        console.log(`🔄 Attempting reconnection in ${delay}ms (attempt ${reconnectAttempts})`);
        
        setTimeout(async () => {
          try {
            await enableNetwork(db);
            reconnectAttempts = 0;
            console.log('✅ Reconnection successful');
          } catch (retryError) {
            console.error('❌ Reconnection failed:', retryError);
            checkConnection();
          }
        }, delay);
      }
    }
  };
  
  setInterval(checkConnection, 30000);
  
  return () => connectionState;
};

const getConnectionState = monitorFirestoreConnection();

// Enhanced auth state monitoring with error handling
auth.onAuthStateChanged(
  (user) => {
    if (user) {
      console.log('🔐 Auth state: User logged in -', user.email);
      
      // ✅ Update PWA session if in PWA mode
      const isPWAMode = localStorage.getItem('pwa_mode') === 'true';
      if (isPWAMode) {
        localStorage.setItem('pwa_last_active', Date.now().toString());
      }
    } else {
      console.log('🔓 Auth state: No user');
    }
  },
  (error) => {
    console.error('❌ Auth state change error:', error);
    
    switch (error.code) {
      case 'auth/network-request-failed':
        console.log('📡 Network request failed - check internet connection');
        break;
      case 'auth/too-many-requests':
        console.log('⏱️ Too many requests - please wait before trying again');
        break;
      default:
        console.log('⚠️ Authentication error:', error.message);
    }
  }
);

// Retry mechanism for Firestore operations
export const withRetry = async (operation, maxAttempts = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (
        error.code === 'permission-denied' ||
        error.code === 'not-found' ||
        error.code === 'already-exists'
      ) {
        throw error;
      }
      
      console.warn(`⚠️ Operation failed (attempt ${attempt}/${maxAttempts}):`, error.message);
      
      if (attempt === maxAttempts) {
        break;
      }
      
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Connection health check
export const checkFirestoreHealth = async () => {
  try {
    const testDoc = doc(db, 'health-check', 'test');
    await getDoc(testDoc);
    return { status: 'healthy', timestamp: new Date().toISOString() };
  } catch (error) {
    return { 
      status: 'unhealthy', 
      error: error.message, 
      timestamp: new Date().toISOString() 
    };
  }
};

// Graceful error handling wrapper
export const safeFirestoreOperation = async (operation, fallbackValue = null) => {
  try {
    return await withRetry(operation);
  } catch (error) {
    console.error('❌ Firestore operation failed:', error);
    
    window.dispatchEvent(new CustomEvent('firestore-error', { 
      detail: { error: error.message, code: error.code } 
    }));
    
    return fallbackValue;
  }
};

// ──────────────────────────────────────────────────────────────────────────────
// Safe popup sign-in helper
// Prevents concurrent popup sign-ins which cause Firebase internal assertion
// "Pending promise was never set". Exports a simple serializer around
// signInWithPopup and normalizes common popup-close errors.
let popupInProgress = false;

export const safeSignInWithPopup = async (provider) => {
  if (popupInProgress) {
    const err = new Error('A sign-in popup is already open');
    err.code = 'auth/popup-in-progress';
    throw err;
  }

  popupInProgress = true;
  try {
    const { signInWithPopup } = await import('firebase/auth');
    const result = await signInWithPopup(auth, provider);
    popupInProgress = false;
    return result;
  } catch (err) {
    const code = err?.code || '';
    const msg = err?.message || '';

    if (
      code === 'auth/popup-closed-by-user' ||
      code === 'auth/cancelled-popup-request' ||
      msg.toLowerCase().includes('popup') ||
      msg.toLowerCase().includes('cancelled')
    ) {
      // Firebase holds an internal lock after popup dismissal — delay release
      // so the next click can open a fresh popup without hitting the internal lock
      setTimeout(() => { popupInProgress = false; }, 500);
      const e = new Error(err.message || 'Popup closed by user');
      e.code = err.code || 'auth/popup-closed-by-user';
      throw e;
    }

    popupInProgress = false;
    throw err;
  }
};

export { 
  auth, 
  googleProvider, 
  db, 
  storage, 
  functions, 
  app,
  getConnectionState,
  isOnline
};